// Observations.jsx - Bug/observation notes with prompt generator
import React, { useState, useEffect } from 'react';
import useActivityLog from '../hooks/useActivityLog';
import './Observations.css';

const Observations = ({ taskId, taskName }) => {
    const [observations, setObservations] = useState([]);
    const [newObservation, setNewObservation] = useState('');
    const [showPrompt, setShowPrompt] = useState(false);
    const { logActivity } = useActivityLog();

    // Load observations from localStorage
    useEffect(() => {
        const saved = localStorage.getItem('fixarr-observations');
        if (saved) {
            try {
                const allObs = JSON.parse(saved);
                setObservations(allObs[taskId] || []);
            } catch (e) {
                console.error('Error loading observations:', e);
            }
        }
    }, [taskId]);

    // Save observation
    const addObservation = () => {
        if (!newObservation.trim()) return;

        const observationText = newObservation.trim();
        const updatedObs = [...observations, {
            id: Date.now(),
            text: observationText,
            date: new Date().toISOString()
        }];

        setObservations(updatedObs);
        setNewObservation('');

        // Save to localStorage
        const saved = localStorage.getItem('fixarr-observations');
        const allObs = saved ? JSON.parse(saved) : {};
        allObs[taskId] = updatedObs;
        localStorage.setItem('fixarr-observations', JSON.stringify(allObs));

        // Log activity
        logActivity({
            type: 'observation',
            action: 'added',
            taskId,
            taskName,
            details: {
                text: observationText.substring(0, 100) // First 100 chars
            }
        });
    };

    // Delete observation
    const deleteObservation = (obsId) => {
        const updatedObs = observations.filter(o => o.id !== obsId);
        setObservations(updatedObs);

        const saved = localStorage.getItem('fixarr-observations');
        const allObs = saved ? JSON.parse(saved) : {};
        allObs[taskId] = updatedObs;
        localStorage.setItem('fixarr-observations', JSON.stringify(allObs));
    };

    // Generate prompt from observations
    const generatePrompt = () => {
        if (observations.length === 0) return '';

        const prompt = `## 🔧 Correcciones Requeridas - Tarea ${taskId}

**Tarea:** ${taskName}

### Observaciones/Bugs detectados:

${observations.map((obs, i) => `${i + 1}. ${obs.text}`).join('\n')}

---

**Instrucción:** Por favor, analiza estas observaciones y realiza las correcciones necesarias en el código. Explica brevemente qué cambios realizas.`;

        return prompt;
    };

    // Copy prompt to clipboard
    const copyPrompt = () => {
        const prompt = generatePrompt();
        navigator.clipboard.writeText(prompt);
        alert('¡Prompt copiado al portapapeles!');
    };

    return (
        <div className="observations-section">
            <h3>🔍 Observaciones</h3>
            <p className="observations-subtitle">Anota bugs o errores que observes durante el trabajo</p>

            {/* Observation Input */}
            <div className="observation-input-wrapper">
                <textarea
                    className="observation-input"
                    placeholder="Describe el bug o error observado..."
                    value={newObservation}
                    onChange={(e) => setNewObservation(e.target.value)}
                    rows={2}
                />
                <button
                    className="observation-add-btn"
                    onClick={addObservation}
                    disabled={!newObservation.trim()}
                >
                    + Añadir
                </button>
            </div>

            {/* Observations List */}
            {observations.length > 0 && (
                <div className="observations-list">
                    {observations.map((obs, index) => (
                        <div key={obs.id} className="observation-item">
                            <span className="observation-number">{index + 1}</span>
                            <span className="observation-text">{obs.text}</span>
                            <button
                                className="observation-delete"
                                onClick={() => deleteObservation(obs.id)}
                                title="Eliminar"
                            >
                                ×
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Generate Prompt Button */}
            {observations.length > 0 && (
                <div className="observations-actions">
                    <button
                        className="generate-prompt-btn"
                        onClick={() => setShowPrompt(!showPrompt)}
                    >
                        {showPrompt ? '🔼 Ocultar Prompt' : '📝 Generar Prompt'}
                    </button>
                    {showPrompt && (
                        <button
                            className="copy-prompt-btn"
                            onClick={copyPrompt}
                        >
                            📋 Copiar
                        </button>
                    )}
                </div>
            )}

            {/* Generated Prompt Preview */}
            {showPrompt && observations.length > 0 && (
                <div className="prompt-preview">
                    <pre>{generatePrompt()}</pre>
                </div>
            )}
        </div>
    );
};

export default Observations;
